import { useRef, useCallback } from 'react';

export interface UseLongPressOptions {
  onLongPress: (e: React.PointerEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  delay?: number; // ms, default: 500
  moveTolerance?: number; // px, default: 10
}

export function useLongPress({
  onLongPress,
  onClick,
  delay = 500,
  moveTolerance = 10,
}: UseLongPressOptions) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const isLongPressRef = useRef(false);
  const lastLongPressTimestampRef = useRef<number>(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startPosRef.current = null;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Primary button only (0) for mouse; ignore secondary/right clicks
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      clearTimer();
      isLongPressRef.current = false;
      startPosRef.current = { x: e.clientX, y: e.clientY };

      timerRef.current = setTimeout(() => {
        isLongPressRef.current = true;
        lastLongPressTimestampRef.current = Date.now();
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator && typeof navigator.vibrate === 'function') {
          try {
            navigator.vibrate(50);
          } catch {
            // Ignore if vibration is restricted
          }
        }
        onLongPress(e);
      }, delay);
    },
    [clearTimer, delay, onLongPress]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!startPosRef.current) return;
      const dx = Math.abs(e.clientX - startPosRef.current.x);
      const dy = Math.abs(e.clientY - startPosRef.current.y);
      if (dx > moveTolerance || dy > moveTolerance) {
        clearTimer();
      }
    },
    [clearTimer, moveTolerance]
  );

  const onPointerUp = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onPointerCancel = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onPointerLeave = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      const now = Date.now();
      // If long press triggered recently (within 400ms) or is active, suppress click
      if (isLongPressRef.current || now - lastLongPressTimestampRef.current < 400) {
        e.preventDefault();
        e.stopPropagation();
        isLongPressRef.current = false;
        return;
      }
      onClick?.(e);
    },
    [onClick]
  );

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    const now = Date.now();
    if (isLongPressRef.current || now - lastLongPressTimestampRef.current < 400) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
    onClick: handleClick,
    onContextMenu: handleContextMenu,
  };
}
